import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/components/ui/select";

/** Die Stufen, die zur Auswahl stehen. Zehn war bis KAN-303 die einzige. */
export const SEITENGROESSEN = [10, 25, 50, 100] as const;

const SPEICHER_SCHLUESSEL = "superadmin.seitengroesse";

/**
 * Die zuletzt gewaehlte Seitengroesse, aus dem Browser.
 *
 * Wer mit hundert Zeilen arbeitet, will das nicht nach jedem Laden neu einstellen. Der
 * Wert liegt nur in diesem Browser und ist unkritisch; faellt der Zugriff aus - privates
 * Fenster, gesperrte Website-Daten -, bleibt es bei der Voreinstellung.
 */
export const gemerkteSeitengroesse = (voreinstellung = 25): number => {
  try {
    const gemerkt = Number(window.localStorage.getItem(SPEICHER_SCHLUESSEL));
    return SEITENGROESSEN.includes(gemerkt as (typeof SEITENGROESSEN)[number]) ? gemerkt : voreinstellung;
  } catch {
    return voreinstellung;
  }
};

const merken = (groesse: number): void => {
  try {
    window.localStorage.setItem(SPEICHER_SCHLUESSEL, String(groesse));
  } catch {
    // Ohne Speicher gilt die Wahl nur fuer diese Sitzung. Kein Grund, etwas zu melden.
  }
};

const Blaetterknopf = ({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 enabled:hover:bg-gray-50 disabled:opacity-40"
  >
    {children}
  </button>
);

/**
 * Das Blaettern unter einer Liste (KAN-303).
 *
 * Vorher standen dort "Seite 1 von 3" und zwei Knoepfe, bei fester Zehnerseite. Wer einen
 * Betrieb suchte, dessen Name ihm nicht einfiel, blaetterte sich durch. Jetzt entscheidet
 * man selbst, wie viele Zeilen man sehen will, und sieht, wo man steht: "1 bis 25 von 58".
 *
 * Erste und letzte Seite sind eigene Knoepfe. Ohne sie ist das Ende einer langen Liste nur
 * ueber wiederholtes Klicken erreichbar, und genau dorthin will man beim Suchen oft.
 */
export const Seitenleiste = ({
  seite,
  seiten,
  gesamt,
  seitengroesse,
  onSeite,
  onSeitengroesse,
  einheit = "Einträgen",
}: {
  /** Nullbasiert, wie in der Tabelle. */
  seite: number;
  seiten: number;
  gesamt: number;
  seitengroesse: number;
  onSeite: (seite: number) => void;
  onSeitengroesse: (groesse: number) => void;
  /** Im Dativ Plural, weil sie hinter "von 58" steht: "Betrieben", "Einträgen". */
  einheit?: string;
}) => {
  const ersteZeile = gesamt === 0 ? 0 : seite * seitengroesse + 1;
  const letzteZeile = Math.min((seite + 1) * seitengroesse, gesamt);
  const amAnfang = seite <= 0;
  const amEnde = seite >= seiten - 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Zeilen pro Seite</span>
        <Select
          value={String(seitengroesse)}
          onValueChange={wert => {
            const groesse = Number(wert);
            merken(groesse);
            onSeitengroesse(groesse);
          }}
        >
          <SelectTrigger className="h-8 w-[72px]" aria-label="Zeilen pro Seite">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SEITENGROESSEN.map(groesse => (
              <SelectItem key={groesse} value={String(groesse)}>
                {groesse}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {/* Nicht nur die Seitenzahl: Wie viele es insgesamt sind, beantwortet die Frage,
            die man beim Blaettern wirklich hat. */}
        <span className="text-sm text-gray-600">
          {gesamt === 0 ? "Nichts gefunden" : `${ersteZeile} bis ${letzteZeile} von ${gesamt} ${einheit}`}
        </span>

        <div className="flex items-center gap-1.5">
          <Blaetterknopf label="Erste Seite" disabled={amAnfang} onClick={() => onSeite(0)}>
            <ChevronsLeft size={16} strokeWidth={2} aria-hidden="true" />
          </Blaetterknopf>
          <Blaetterknopf label="Vorherige Seite" disabled={amAnfang} onClick={() => onSeite(seite - 1)}>
            <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
          </Blaetterknopf>
          <span className="px-1 text-sm tabular-nums text-gray-600">
            Seite {Math.min(seite + 1, Math.max(seiten, 1))} von {Math.max(seiten, 1)}
          </span>
          <Blaetterknopf label="Nächste Seite" disabled={amEnde} onClick={() => onSeite(seite + 1)}>
            <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
          </Blaetterknopf>
          <Blaetterknopf label="Letzte Seite" disabled={amEnde} onClick={() => onSeite(seiten - 1)}>
            <ChevronsRight size={16} strokeWidth={2} aria-hidden="true" />
          </Blaetterknopf>
        </div>
      </div>
    </div>
  );
};

export default Seitenleiste;
